import React, { useEffect, useRef } from 'react';

// Gauge.js is loaded globally from /vendor/gaugejs/gauge.min.js
declare const Gauge: any;

export interface GaugeChartProps {
  /** Value to display (-100 to 100) */
  value: number;
  /** Title text to display */
  title?: string;
}

function getTextType(value: number): 'negative' | 'positive' | 'neutral' {
  if (value < -25) return 'negative';
  if (value > 25) return 'positive';
  return 'neutral';
}

export function GaugeChart({ value, title }: GaugeChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gaugeRef = useRef<any>(null);
  const textType = getTextType(value);

  useEffect(() => {
    if (!canvasRef.current || typeof Gauge === 'undefined') return;

    // Initialize gauge
    gaugeRef.current = new Gauge(canvasRef.current).setOptions({
      angle: 0.0,
      lineWidth: 0.3,
      radiusScale: 1,
      pointer: {
        length: 0.6,
        strokeWidth: 0.035,
        color: '#000000',
      },
      staticLabels: {
        font: '10px sans-serif',
        labels: [-100, -75, -50, -25, 0, 25, 50, 75, 100],
        color: '#000000',
        fractionDigits: 0,
      },
      staticZones: [
        { strokeStyle: 'rgb(255, 153, 170)', min: -100, max: -50 },
        { strokeStyle: 'rgb(255, 204, 170)', min: -75, max: -25 },
        { strokeStyle: 'rgb(255, 255, 170)', min: -25, max: 25 },
        { strokeStyle: 'rgb(204, 255, 170)', min: 25, max: 75 },
        { strokeStyle: 'rgb(153, 255, 170)', min: 75, max: 100 },
      ],
      limitMax: false,
      limitMin: false,
      colorStart: '#6F6EA0',
      colorStop: '#C0C0DB',
      strokeColor: '#EEEEEE',
      generateGradient: true,
      highDpiSupport: true,
    });

    gaugeRef.current.maxValue = 100;
    gaugeRef.current.setMinValue(-100);
    gaugeRef.current.animationSpeed = 10;
    gaugeRef.current.set(value);

    return () => {
      gaugeRef.current = null;
    };
  }, []);

  // Update value when it changes
  useEffect(() => {
    if (gaugeRef.current) {
      gaugeRef.current.set(value);
    }
  }, [value]);

  return (
    <div className="gauge">
      {title && <div className="gauge-title">{title}</div>}
      <h2 className={`text-center gauge-text gauge-text--${textType}`}>
        {Math.round(value)}
      </h2>
      <canvas ref={canvasRef} className="gauge-body" />
    </div>
  );
}

export default GaugeChart;
